/* eslint-disable @typescript-eslint/no-explicit-any */
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import dayjs from 'dayjs';

(pdfMake as any).vfs = (pdfFonts as any).pdfMake.vfs;

export interface IComprovanteMensalidadeDados {
  idmensalidade: string;
  nomeempresa: string;
  fantasiaempresa: string;
  mes: number;
  ano: number;
  valor: number;
  datapagamento: string;
  formapagamento: string;
}

const MESES_ABREV = ['JAN', 'FEV', 'MAR', 'ABR', 'MAI', 'JUN', 'JUL', 'AGO', 'SET', 'OUT', 'NOV', 'DEZ'];

const formatarMoeda = (valor: number) =>
  Number(valor || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

// Número de referência do comprovante: derivado do uuid do lançamento (idmensalidade) em
// vez de um contador à parte, então não exige nenhum campo/sequência nova no banco e duas
// impressões do mesmo lançamento sempre mostram o mesmo número. Os 8 primeiros dígitos hex
// do uuid viram um número decimal (em vez de mostrar letras A-F), formatado em dois blocos.
const gerarNumeroReferencia = (idmensalidade: string) => {
  const hex = idmensalidade.replace(/-/g, '').slice(0, 8);
  const numero = parseInt(hex, 16).toString().padStart(10, '0');
  return `${numero.slice(0, 5)}-${numero.slice(5)}`;
};

// Logo padrão do comprovante: um ícone de recibo desenhado em SVG (em vez de rasterizado
// em canvas), já que quem emite esse comprovante é o próprio SIAC, não o cliente, então
// não depende de logo cadastrada de nenhuma empresa. Sendo SVG vetorial descrito aqui
// mesmo, sempre existe (nada pode falhar em tempo de execução como um canvas sem
// contexto 2D) e fica nítido em qualquer resolução de impressão.
const LOGO_COMPROVANTE_SVG = `
<svg width="64" height="64" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
  <rect width="64" height="64" rx="14" fill="#000000"/>
  <path d="M18 12h28v38l-3.5-2.6-3.5 2.6-3.5-2.6-3.5 2.6-3.5-2.6-3.5 2.6-3.5-2.6-3.5 2.6V12z" fill="#ffffff"/>
  <rect x="23" y="20" width="18" height="2.6" rx="1.3" fill="#9ca3af"/>
  <rect x="23" y="27" width="18" height="2.6" rx="1.3" fill="#9ca3af"/>
  <rect x="23" y="34" width="11" height="2.6" rx="1.3" fill="#10b981"/>
</svg>`.trim();

// Comprovante da baixa de uma mensalidade, no formato de um "canhoto" de carnê (mesmo
// visual do carnê de venda — impressaocarne.ts): moldura única, cabeçalho com logo +
// selos de emissão/referência, corpo com os dados do pagamento e duas linhas de
// assinatura (responsável do sistema e cliente). Retorna o Blob (em vez de abrir direto)
// pra ser exibido embutido no DialogVisualizarComprovante, na própria tela.
// Margens da página: pequenas para o comprovante ocupar praticamente toda a largura do A4
// (595.28pt). As larguras dos elementos de canvas abaixo (barra superior, linha divisória)
// são fixas em pt, então precisam ser recalculadas junto caso PAGE_MARGIN_X mude.
const PAGE_MARGIN_X = 24;
const PAGE_MARGIN_Y = 28;
const CONTENT_WIDTH = 595.28 - PAGE_MARGIN_X * 2 - /* borda do card */ 2 - /* margem interna do miolo */ 20;

export async function gerarComprovanteMensalidade(dados: IComprovanteMensalidadeDados): Promise<Blob> {
  const nomeEmpresa = dados.fantasiaempresa || dados.nomeempresa;
  const mesAno = `${MESES_ABREV[dados.mes - 1]}/${dados.ano}`;

  const miolo: any = {
    stack: [
      { canvas: [{ type: 'rect', x: 0, y: 0, w: CONTENT_WIDTH, h: 4, color: '#000000' }], margin: [0, 0, 0, 12] },

      {
        columns: [
          { svg: LOGO_COMPROVANTE_SVG, width: 42, alignment: 'left' },
          {
            width: '*',
            stack: [
              { text: 'COMPROVANTE DE PAGAMENTO', bold: true, fontSize: 12, color: '#000000' },
              { text: 'Mensalidade do sistema SIAC', fontSize: 7, color: '#000000', margin: [0, 2, 0, 0] },
            ],
            margin: [10, 8, 0, 0],
          },
          {
            width: 95,
            table: {
              body: [
                [{ text: 'EMISSÃO', fontSize: 6, alignment: 'center', color: '#000000', bold: true }],
                [{ text: dayjs().format('DD/MM/YYYY'), fontSize: 8, alignment: 'center', color: '#000000' }],
              ],
            },
            layout: {
              fillColor: () => '#f3f4f6',
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingTop: () => 3,
              paddingBottom: () => 3,
            },
          },
          {
            width: 82,
            table: {
              widths: ['*'],
              body: [
                [{ text: 'REFERÊNCIA', fontSize: 6, alignment: 'center', color: '#cbd5e1', margin: [0, 4, 0, 0] }],
                [{ text: mesAno, fontSize: 15, bold: true, alignment: 'center', color: 'white', margin: [0, 0, 0, 4] }],
              ],
            },
            layout: {
              fillColor: () => '#000000',
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingLeft: () => 2,
              paddingRight: () => 2,
              paddingTop: () => 0,
              paddingBottom: () => 0,
            },
          },
        ],
        columnGap: 6,
        margin: [0, 0, 0, 8],
      },

      { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH, y2: 0, lineWidth: 1, lineColor: '#000000' }], margin: [0, 0, 0, 8] },

      {
        columns: [
          {
            width: '*',
            stack: [
              { text: [{ text: 'Cliente: ', bold: true }, nomeEmpresa], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'Referente a: ', bold: true }, mesAno], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'Forma de pgto: ', bold: true }, dados.formapagamento], fontSize: 9, margin: [0, 0, 0, 3] },
              { text: [{ text: 'Nº de referência: ', bold: true }, gerarNumeroReferencia(dados.idmensalidade)], fontSize: 9, color: '#000000' },
            ],
          },
          {
            width: 190,
            table: {
              widths: ['*'],
              body: [
                [{ text: 'VALOR', fontSize: 7, bold: true, color: '#000000', alignment: 'right', margin: [0, 8, 10, 2] }],
                [{ text: formatarMoeda(dados.valor), fontSize: 19, bold: true, color: '#000000', alignment: 'right', margin: [0, 0, 10, 10] }],
              ],
            },
            layout: {
              fillColor: () => '#f3f4f6',
              hLineWidth: () => 0,
              vLineWidth: () => 0,
            },
          },
        ],
        columnGap: 10,
        margin: [0, 0, 0, 22],
      },

      {
        columns: [
          {
            width: '*',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH / 2 - 15, y2: 0, lineWidth: 0.5, lineColor: '#000000' }], margin: [0, 14, 0, 2] },
              { text: 'Assinatura do responsável pelo sistema', fontSize: 7, color: '#000000' },
            ],
          },
          {
            width: '*',
            alignment: 'right',
            stack: [
              { canvas: [{ type: 'line', x1: 0, y1: 0, x2: CONTENT_WIDTH / 2 - 15, y2: 0, lineWidth: 0.5, lineColor: '#000000' }], margin: [0, 14, 0, 2], alignment: 'right' },
              { text: 'Assinatura do Cliente', fontSize: 7, color: '#000000', alignment: 'right' },
            ],
          },
        ],
        margin: [0, 0, 0, 20],
      },

      { text: 'Este comprovante não possui valor fiscal.', fontSize: 7, italics: true, color: '#000000', alignment: 'center' },
    ],
    margin: [10, 10, 10, 10],
  };

  const card: any = {
    table: { widths: ['*'], body: [[miolo]] },
    layout: {
      hLineWidth: () => 1,
      vLineWidth: () => 1,
      hLineColor: () => '#000000',
      vLineColor: () => '#000000',
      paddingLeft: () => 0,
      paddingRight: () => 0,
      paddingTop: () => 0,
      paddingBottom: () => 0,
    },
  };

  const documentDefinition: any = {
    pageSize: 'A4',
    pageMargins: [PAGE_MARGIN_X, PAGE_MARGIN_Y, PAGE_MARGIN_X, PAGE_MARGIN_Y],
    content: [card],
  };

  return new Promise((resolve, reject) => {
    try {
      const pdfDocGenerator = pdfMake.createPdf(documentDefinition);
      pdfDocGenerator.getBlob((blob: Blob) => resolve(blob));
    } catch (error) {
      reject(error);
    }
  });
}
